import { useEffect, useLayoutEffect, useState } from "react";
import styled, { keyframes } from "styled-components";

// Shared loading primitives. Use <Skeleton/> placeholders shaped like the real
// content so layout height is reserved (no pop-in / layout shift), then wrap the
// loaded content in <FadeIn> so it appears all-at-once instead of piecemeal.

const shimmer = keyframes`
  0% { background-position: -700px 0; }
  100% { background-position: 700px 0; }
`;

const Block = styled.div`
  display: ${({ $inline }) => ($inline ? "inline-block" : "block")};
  width: ${({ $w }) => $w};
  height: ${({ $h }) => $h};
  border-radius: ${({ $circle, $r }) => ($circle ? "50%" : $r)};
  margin-bottom: ${({ $mb }) => $mb};
  flex-shrink: 0;
  background-color: var(--skeleton);
  background-image: linear-gradient(
    90deg,
    var(--skeleton) 0%,
    var(--skeleton-sheen) 50%,
    var(--skeleton) 100%
  );
  background-size: 700px 100%;
  background-repeat: no-repeat;
  animation: ${shimmer} 1.3s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const px = (v) => (typeof v === "number" ? `${v}px` : v);

/**
 * A single shimmering placeholder block.
 * @param {string|number} width  - default "100%"
 * @param {string|number} height - default 14
 * @param {string} radius        - default "8px"
 * @param {string|number} mb     - margin-bottom, default 0
 * @param {boolean} circle       - render as a circle (radius 50%)
 * @param {boolean} inline       - inline-block instead of block
 */
export default function Skeleton({
  width = "100%",
  height = 14,
  radius = "8px",
  mb = 0,
  circle = false,
  inline = false,
  style,
  className,
}) {
  return (
    <Block
      $w={px(width)}
      $h={px(height)}
      $r={radius}
      $mb={px(mb)}
      $circle={circle}
      $inline={inline}
      style={style}
      className={className}
      aria-hidden="true"
      data-skeleton=""
    />
  );
}

// Multi-line text placeholder. Last line is shorter for a natural look.
export function SkeletonText({ lines = 3, height = 12, gap = 8, lastWidth = "60%" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: px(gap) }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height={height}
          width={i === lines - 1 ? lastWidth : "100%"}
        />
      ))}
    </div>
  );
}

// Wrap loaded content so it fades in as a finished unit (no progressive pop-in).
export const FadeIn = styled.div`
  animation: skFade 0.28s ease both;
  @keyframes skFade {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * How many placeholder rows fill the space a list has on screen, so a loading list
 * reads as a full page rather than a few stub rows.
 *
 * Renders `min` rows first, then measures the container (`ref`): one row's height plus
 * the container's row gap (the first child, or `rowSelector` when the first child is a
 * header), its column count when it is a CSS grid, and the space left — inside the
 * container when it scrolls, otherwise from its top to the bottom of the viewport.
 * Re-measures on resize. Pass `enabled: false` once real rows are in.
 */
export function useFillCount(
  ref,
  { min = 3, max = 60, bottomGap = 24, enabled = true, rowSelector = null } = {},
) {
  const [count, setCount] = useState(min);
  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const row = (rowSelector && el.querySelector(rowSelector)) || el.firstElementChild;
      if (!row) return;
      const cs = getComputedStyle(el);
      const rowH = row.getBoundingClientRect().height + (parseFloat(cs.rowGap) || 0);
      if (!(rowH > 0)) return;
      const cols =
        cs.display === "grid"
          ? cs.gridTemplateColumns.split(" ").filter(Boolean).length || 1
          : 1;
      const scrolls = cs.overflowY === "auto" || cs.overflowY === "scroll";
      const avail = scrolls
        ? el.clientHeight
        : window.innerHeight - el.getBoundingClientRect().top - bottomGap;
      const rows = Math.floor(avail / rowH);
      setCount(Math.max(min, Math.min(max, rows * cols)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [ref, min, max, bottomGap, enabled, rowSelector]);
  return count;
}

// True once `active` has held for `delay` ms, so a fast load never flashes a skeleton.
export function useDelayedFlag(active, delay = 200) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!active) {
      setShown(false);
      return undefined;
    }
    const t = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(t);
  }, [active, delay]);
  return active && shown;
}
